import { AddTagsForSamples, SampleTrackingSampleInformation, SelectedSampleInfo } from '../../data/SampleTrackingData';
import React, { SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import useMemoTranslation from '../../hooks/UseMemoTranslation';
import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GridColDef,
  GridRowId,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { filter, flatMap, flatten, groupBy, map, uniq, uniqBy } from 'lodash';
import {
  checkByStatus,
  currentOrder,
  patientDeidentity,
  qcStatus,
  researchProject,
  sampleId,
  sampleIdentifier,
  transition,
} from '../../util/Constants';
import { renderSampleHierarchyGridCell } from '../../components/grid/cell/SampleHierarchyGridCell';
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { CompactGridWrapper } from '../../components/grid/CompactGridWrapper';
import { LoadingState, LoadState } from '../../components/LoadingStateUtil';
import { GetSampleTags, getTransitionSampleIdentifier, SampleTag } from '../../data/SampleData';
import useAuth from '../../auth/UseAuth';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { CustomizableSwitch } from '../../components/CustomizableSwitch';
import { MultipleValueSelector } from '../../components/MultipleValueSelector';
import { AutocompleteValue } from '@mui/base/AutocompleteUnstyled/useAutocomplete';
import { CancelButton } from '../../components/CancelButton';
import { PrimaryButton } from '../../components/PrimaryButton';
import { DialogOpenButton } from '../../components/DialogOpenButton';
import { CallSplit } from '@mui/icons-material';
import { CheckPatientSamplesAlreadyProcessed } from '../../data/SampleHierarchyData';
import { LightTooltip } from '../../components/grid/cell/GridCellTooltip';
import { PersistentDialog } from '../../components/PersistentDialog';
import {
  SelectableCandidate,
  SelectableEntity,
  SelectableFieldGridColTypeMapper,
  SelectedData,
  SelectFields,
} from '../../data/SampleTrackingFieldSelectionData';
import { MemoizedColumnSelectorDialog } from '../../components/TableLineageColumnSelectorDialog';
import { formatDate, jsonIsEqual } from '../../util/grid/TableUtils';
import { GridColumnVisibilityModel } from '@mui/x-data-grid/hooks/features/columns/gridColumnsInterfaces';
import { v4 as uuidv4 } from 'uuid';

const CURRENT_VIEW_ENTITY = 'current_view';

type SampleDetail = {
  sampleId: string;
  entryId?: string | undefined;
  sampleIdentifier: string;
  researchProject: string;
  transition: string;
  checkByStatus?: string | undefined;
  qcStatus?: string | undefined;
};

type DuplicateSampleDetailsProps = {
  details: SampleDetail[];
  columns: GridColDef[];
  selectedEntities?: ReadonlyArray<SelectableEntity>;
  allColumnEntities?: ReadonlyArray<SelectableEntity>;
  additionalData?: SelectedData | undefined;
};

const DuplicateSampleDetails = ({
  details,
  columns,
  additionalData,
  selectedEntities = [],
  allColumnEntities = [],
}: DuplicateSampleDetailsProps) => {
  const [defaultColumns, setDefaultColumns] = useState<GridColDef[]>(columns);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

  const allFields = useMemo(() => flatMap(allColumnEntities, i => i.fields), [allColumnEntities]);
  const propColumnFieldNames = useMemo(() => map(columns, i => i.field), [columns]);
  const selectedFields = useMemo(() => flatMap(selectedEntities, i => i.fields), [selectedEntities]);
  const additionalSampleData = useMemo(() => {
    if (additionalData && additionalData.data) {
      return additionalData;
    }
    return {
      data: {},
      columns: [],
      entries: [],
    };
  }, [additionalData]);

  // set up the actual column definitions, based on the ones provided outright, and the ones provided by selectable entities
  useEffect(() => {
    setDefaultColumns(prevState => {
      const newColumns = [
        ...columns,
        ...(map(
          filter(allFields, i => !propColumnFieldNames.includes(i.key)),
          column => {
            const columnType = SelectableFieldGridColTypeMapper[column.type];

            return {
              field: column.key,
              headerName: column.fullDisplayName,
              sortable: true,
              minWidth: 130,
              type: columnType,
              valueGetter: ({ row }) => {
                if (row?.additionalData && row?.additionalData[column.key]) {
                  const rowValue = row?.additionalData[column.key];
                  return column.type === 'date' || column.type === 'dateTime' ? new Date(rowValue) : rowValue;
                }
              },
              valueFormatter: ({ value }) =>
                (column.type === 'date' || column.type === 'dateTime') && value ? formatDate(value) : value,
            };
          }
        ) as GridColDef[]),
      ];

      if (jsonIsEqual(prevState, newColumns)) {
        return prevState;
      }

      return newColumns;
    });
  }, [columns, allFields, propColumnFieldNames, selectedFields]);

  // set up the column visibility model, based on the selected fields and the provided columns
  useEffect(() => {
    const visibility: GridColumnVisibilityModel = {};

    allFields.forEach(field => {
      visibility[field.key] = false;
    });
    propColumnFieldNames.forEach(fieldName => {
      visibility[fieldName] = false;
    });

    selectedFields.forEach(field => {
      visibility[field.key] = true;
    });

    setColumnVisibilityModel(prevState => {
      if (jsonIsEqual(prevState, visibility)) {
        return prevState;
      }
      return visibility;
    });
  }, [allFields, propColumnFieldNames, selectedFields]);

  const rows = useMemo(() => {
    return details.map((detail, index) => {
      return {
        id: index,
        ...detail,
        additionalData: detail.entryId ? additionalSampleData.data[detail.entryId] : {},
      };
    });
  }, [details, additionalSampleData]);

  const apiRef = useGridApiRef();

  return (
    <Box display='flex' flexDirection='row' alignItems='center' mb={1} justifyContent='space-between' padding={'10px'}>
      <CompactGridWrapper
        apiRef={apiRef}
        columns={defaultColumns}
        rows={rows}
        getRowId={row => row.id}
        hideFooter={true}
        defaultGroupingExpansionDepth={0}
        getRowHeight={() => 'auto'} // This makes rows grow to fit vertically
        disableRowSelectionOnClick
        disableColumnSelector
        disableColumnReorder={false}
        columnsToAutoSize={[sampleIdentifier, researchProject, transition]}
        columnVisibilityModel={columnVisibilityModel}
      />
    </Box>
  );
};

type DuplicateSampleRow = {
  sampleId: string;
  entryId: string;
  sampleIdentifier: string;
  patientDeidentity: string;
  siblings: SampleDetail[];
  duplicateSelected?: boolean;
};

const DuplicateSamplesGrid = ({
  samples,
  setSelectedSampleIds,
  loadingState,
  researchProjectId,
}: {
  samples: ReadonlyArray<DuplicateSampleRow>;
  setSelectedSampleIds?: React.Dispatch<React.SetStateAction<string[]>> | undefined;
  loadingState: LoadingState;
  researchProjectId: string;
}) => {
  const { t } = useMemoTranslation();

  const rows = useMemo(
    () =>
      samples.map((sample, index) => ({
        id: index,
        ...sample,
      })),
    [samples]
  );

  const [pageSize, setPageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>();
  const [expandedRowIds, setExpandedRowIds] = useState<GridRowId[]>([]);

  const [allColumnEntities, setAllColumnEntities] = useState<ReadonlyArray<SelectableEntity>>([]);
  const [entitiesFromProvidedColumns, setEntitiesFromProvidedColumns] = useState<SelectableEntity[]>([]);
  const [currentSelectedColumnEntities, setCurrentSelectedColumnEntities] = useState<ReadonlyArray<SelectableEntity>>(
    []
  );

  const [selectedFieldData, setSelectedFieldData] = useState<SelectedData>();

  const { accessToken } = useAuth();
  const apiRef = useGridApiRef();

  useEffect(() => {
    // Reset expanded row ids when details change
    setExpandedRowIds(rows.map(row => row.id));
  }, [rows]);

  useEffect(() => {
    if (!selectionModel || !setSelectedSampleIds) {
      return;
    }

    const sampleIds: string[] = [];
    selectionModel.forEach(selected => {
      const id = apiRef.current?.getRow(selected)?.sampleId;
      if (id) {
        sampleIds.push(id);
      }
    });

    setSelectedSampleIds(uniq(sampleIds));
  }, [apiRef, setSelectedSampleIds, selectionModel]);

  const handleExpandCollapse = useCallback(() => {
    if (expandedRowIds.length === rows.length) {
      setExpandedRowIds([]);
    } else {
      setExpandedRowIds(rows.map(row => row.id));
    }
  }, [expandedRowIds, rows]);

  useEffect(() => {
    if (currentSelectedColumnEntities.length === 0 || samples.length === 0 || !accessToken) {
      return;
    }

    const selectFields = async () => {
      const candidates = uniqBy(
        samples.flatMap(sample => {
          return sample.siblings.map(sibling => {
            return {
              sampleId: sibling.sampleId,
              entryId: sibling.entryId,
            } as SelectableCandidate;
          });
        }),
        c => c.entryId
      );

      const payload = {
        entities: currentSelectedColumnEntities,
        candidates: candidates,
        researchProjectId: researchProjectId,
      };
      const data = await SelectFields(payload, accessToken);

      setSelectedFieldData(data);
    };

    selectFields();
  }, [samples, currentSelectedColumnEntities, accessToken, researchProjectId]);

  const useColumns = (): GridColDef[] => {
    return useMemo(
      () => [
        {
          field: 'warning',
          headerName: '',
          align: 'center',
          hidden: false,
          filterable: false,
          disableColumnMenu: true,
          width: 50,
          resizable: false,
          renderCell: params =>
            params.row.duplicateSelected ? (
              <LightTooltip title={t('duplicateSampleSelectedWarning')}>
                <WarningAmberIcon color='warning' sx={{ width: '20px', height: '20px', marginLeft: '10px' }} />
              </LightTooltip>
            ) : null,
          valueGetter: params => params.row.duplicateSelected,
          sortComparator: (v1, v2, sortParams1, sortParams2) => {
            return Number(v2) - Number(v1);
          },
        },
        {
          field: patientDeidentity,
          headerName: t(patientDeidentity),
          headerAlign: 'left',
          align: 'left',
          width: 350,
          hideable: false,
          disableReorder: true,
          valueGetter: params => params.row.patientDeidentity,
        },
        {
          field: sampleIdentifier,
          headerName: t('sampleIdentifierFull'),
          headerAlign: 'left',
          align: 'left',
          minWidth: 400,
          maxWidth: 800,
          hideable: false,
          disableReorder: true,
          renderCell: params =>
            renderSampleHierarchyGridCell({
              ...params,
              sampleId: params.row.sampleId,
              displayValue: params.value,
              defaultShowGlobalHistory: true,
            }),
          valueGetter: params => params.row.sampleIdentifier,
        },
        {
          field: sampleId,
          headerName: t(sampleId),
          headerAlign: 'left',
          align: 'left',
          width: 400,
          minWidth: 250,
          hideable: false,
          disableReorder: true,
          valueGetter: params => params.row.sampleId,
          flex: 1,
        },
      ],
      []
    ) as GridColDef[];
  };

  const columns = useColumns();
  const useSampleInfoColumns = (): GridColDef[] => {
    return useMemo(
      () =>
        [
          {
            field: sampleIdentifier,
            headerName: t('sampleIdentifierFull'),
            headerAlign: 'left',
            align: 'left',
            width: 400,
            maxWidth: 800,
            renderCell: params =>
              renderSampleHierarchyGridCell({
                ...params,
                sampleId: params.row.sampleId,
                displayValue: params.value,
                defaultShowGlobalHistory: true,
              }),
            valueGetter: params => params.row.sampleIdentifier,
          },
          {
            field: sampleId,
            headerName: t(sampleId),
            headerAlign: 'left',
            align: 'left',
            width: 300,
            minWidth: 250,
            valueGetter: params => params.row.sampleId,
          },
          {
            field: researchProject,
            headerName: t(researchProject),
            hideable: false,
            headerAlign: 'left',
            align: 'left',
            disableReorder: true,
            valueGetter: params => params.row.researchProject,
          },
          {
            field: transition,
            headerName: t(currentOrder),
            headerAlign: 'left',
            align: 'left',
            valueGetter: params => params.row.transition,
            flex: 1,
          },
          {
            field: checkByStatus,
            headerName: t(checkByStatus),
            headerAlign: 'left',
            align: 'left',
            valueGetter: params => params.row.checkByStatus,
            flex: 1,
          },
          {
            field: qcStatus,
            headerName: t(qcStatus),
            headerAlign: 'left',
            align: 'left',
            valueGetter: params => params.row.qcStatus,
            flex: 1,
          },
        ] as GridColDef[],
      []
    );
  };

  const sampleInfoColumns = useSampleInfoColumns();
  const defaultColumns = useMemo(() => {
    return sampleInfoColumns.filter(col => col.headerName?.trim()).map(col => col.field);
  }, [sampleInfoColumns]);

  const setEntitiesCallback = useCallback((entities: SetStateAction<ReadonlyArray<SelectableEntity>>) => {
    setCurrentSelectedColumnEntities(entities);
  }, []);

  useEffect(() => {
    const currentViewEntities = [
      {
        name: CURRENT_VIEW_ENTITY,
        displayName: 'Current View',
        fields: sampleInfoColumns
          .filter(col => col.headerName?.trim())
          .map(col => ({
            columnDisplayName: col.headerName ?? col.field,
            columnName: col.field,
            fullDisplayName: col.headerName ?? col.field,
            key: col.field,
            type: col.type ?? 'text',
          })),
      },
    ] as SelectableEntity[];

    setEntitiesFromProvidedColumns(currentViewEntities);
    setCurrentSelectedColumnEntities(currentViewEntities);
  }, [sampleInfoColumns]);

  return (
    <Stack direction={'column'} gap={1} sx={{ width: '100%' }}>
      <Typography variant='subtitle1'>{t('checkForDuplicateSamplesInfo')}</Typography>
      {rows.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Button
            sx={{
              width: 'auto',
              textTransform: 'uppercase',
              padding: 0,
              minWidth: 'auto',
              justifyContent: 'flex-start',
            }}
            variant={'text'}
            onClick={handleExpandCollapse}
          >
            {t('expandCollapseAllRows')}
          </Button>
          <MemoizedColumnSelectorDialog
            setAdditionalColumnEntities={setEntitiesCallback}
            setAllColumnEntities={setAllColumnEntities}
            defaultColumns={defaultColumns}
            entitiesFromProvidedColumns={entitiesFromProvidedColumns}
            cacheKey={'duplicate-samples-grid'}
            selectedEntities={currentSelectedColumnEntities}
          />
        </Box>
      )}
      <CompactGridWrapper
        columns={columns}
        apiRef={apiRef}
        rows={rows}
        autoHeight={true}
        density={'compact'}
        paginationMode={'client'}
        getDetailPanelContent={({ row }) =>
          row.siblings && row.siblings.length > 0 ? (
            <DuplicateSampleDetails
              columns={sampleInfoColumns}
              details={row.siblings}
              selectedEntities={currentSelectedColumnEntities}
              allColumnEntities={allColumnEntities}
              additionalData={selectedFieldData}
            />
          ) : null
        }
        getDetailPanelHeight={_ => 'auto'} // Height based on the content.
        detailPanelExpandedRowIds={expandedRowIds}
        onDetailPanelExpandedRowIdsChange={setExpandedRowIds}
        getRowHeight={() => 'auto'}
        onPaginationModelChange={model => {
          setPage(model.page);
          setPageSize(model.pageSize);
        }}
        initialState={{
          pinnedColumns: {
            left: [GRID_CHECKBOX_SELECTION_FIELD],
          },
        }}
        checkboxSelection={true}
        checkboxSelectionVisibleOnly={false}
        disableRowSelectionOnClick={true}
        onRowSelectionModelChange={newSelectionModel => setSelectionModel(newSelectionModel)}
        rowSelectionModel={selectionModel}
        paginationModel={{ pageSize: pageSize, page: page }}
        pagination={true}
        pageSizeOptions={[10]}
        disableColumnSelector={true}
        loadingState={loadingState}
      />
    </Stack>
  );
};

const renderTagSelection = (
  title: string,
  checked: boolean,
  setChecked: React.Dispatch<SetStateAction<boolean>>,
  selectedValue: SampleTag[],
  setSelectedValue: React.Dispatch<SetStateAction<SampleTag[]>>,
  options: SampleTag[],
  loadingState: LoadingState,
  sx = {}
) => {
  return (
    <Stack direction={'row'} gap={2} sx={{ justifyContent: 'flex-start', alignItems: 'center', paddingTop: '5px' }}>
      <CustomizableSwitch
        title={title}
        checked={checked}
        onChange={(_, checked) => setChecked(checked)}
        sx={{ justifyContent: 'flex-end', ...sx }}
      />
      <MultipleValueSelector<SampleTag>
        filterSelectedOptions={true}
        value={selectedValue}
        options={options}
        getOptionLabel={option => `${(option as SampleTag).name}`}
        onChange={(
          _: React.SyntheticEvent,
          value: AutocompleteValue<SampleTag, boolean, boolean, boolean>,
          __: AutocompleteChangeReason,
          ___: AutocompleteChangeDetails<SampleTag> | undefined
        ) => {
          setSelectedValue(value as SampleTag[]);
        }}
        variant={'outlined'}
        loadingState={loadingState}
        label={'Select tag(s)'}
        disabled={!checked}
        sx={{ width: '60%' }}
      />
    </Stack>
  );
};

export type DuplicateSamplesGridModalProps = {
  samplesToCheck: ReadonlyArray<SelectedSampleInfo>;
  researchProjectId: string;
  onCancel?: () => void;
  onConfirm?: (
    selectedSampleIds: string[],
    unselectedSampleIds: string[],
    reloadData: boolean,
    noResultsFound: boolean
  ) => void | Promise<void> | undefined;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>> | undefined;
  autoConfirmWhenNoResultsFound?: boolean;
};

export const SampleTrackingDuplicateSamplesModal = ({
  samplesToCheck,
  researchProjectId,
  onConfirm,
  open = false,
  setOpen = undefined,
  onCancel = () => undefined,
  autoConfirmWhenNoResultsFound = false,
}: DuplicateSamplesGridModalProps) => {
  const [loadingState, setLoadingState] = useState<LoadingState>({ status: 'NotStarted' });
  const sampleIds = useMemo(() => (samplesToCheck ?? []).map(s => s.sampleId), [samplesToCheck]);
  const [rawData, setRawData] = useState<
    { [key: string]: ReadonlyArray<SampleTrackingSampleInformation> } | undefined
  >();

  const samplesByPatientDeidentity = useMemo(
    () => groupBy(samplesToCheck ?? [], s => s.patientDeidentity),
    [samplesToCheck]
  );

  const [selectedSampleIds, setSelectedSampleIds] = useState<string[]>([]);
  const [tagSelectedSamples, setTagSelectedSamples] = useState<boolean>(false);
  const [tagUnselectedSamples, setTagUnselectedSamples] = useState<boolean>(false);
  const [sampleTagOptions, setSampleTagOptions] = useState<ReadonlyArray<SampleTag>>([]);
  const [selectedSampleTags, setSelectedSampleTags] = useState<SampleTag[]>([]);
  const [unselectedSampleTags, setUnselectedSampleTags] = useState<SampleTag[]>([]);
  const willTagSamples = useMemo(
    () =>
      (tagSelectedSamples && selectedSampleTags.length > 0) ||
      (tagUnselectedSamples && unselectedSampleTags.length > 0),
    [tagSelectedSamples, selectedSampleTags, tagUnselectedSamples, unselectedSampleTags]
  );
  const [samplesToReview, setSamplesToReview] = useState<ReadonlyArray<DuplicateSampleRow> | undefined>();

  const displayOpenControl = useMemo(() => setOpen === undefined, [setOpen]);

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const { accessToken } = useAuth();
  const { t } = useMemoTranslation();

  const actionButtonLabel = useMemo(() => {
    if (displayOpenControl) {
      return willTagSamples ? t('updateSelectionAndTag') : t('updateSelection');
    } else {
      return willTagSamples ? t('submitOrderAndTag') : t('submitOrder');
    }
  }, [t, displayOpenControl, willTagSamples]);

  const noSamplesSelected = useMemo(() => {
    if (samplesToReview === undefined) {
      return false;
    }

    // no samples selected, all possible samples are to be reviewed
    return (
      selectedSampleIds &&
      selectedSampleIds.length === 0 &&
      sampleIds.every(sampleId => new Set(samplesToReview.map(s => s.sampleId)).has(sampleId))
    );
  }, [sampleIds, selectedSampleIds, samplesToReview]);

  const resetDialog = useCallback(() => {
    if (setOpen) {
      setOpen(false);
    } else {
      setModalOpen(false);
    }
    setTagUnselectedSamples(false);
    setTagSelectedSamples(false);
    setLoadingState({ status: 'NotStarted' });
    setSelectedSampleTags([]);
    setUnselectedSampleTags([]);
    setSelectedSampleIds([]);
    if (displayOpenControl) {
      setRawData(undefined);
      setSamplesToReview(undefined);
    }
  }, [displayOpenControl, setOpen]);

  const handleCancel = () => {
    onCancel?.();
    resetDialog();
  };

  const handleConfirm = useCallback(async () => {
    if (willTagSamples && samplesToReview) {
      const unselectedSampleIds = samplesToReview
        .filter(s => !selectedSampleIds.includes(s.sampleId))
        .map(s => s.sampleId);

      const tagRequests = selectedSampleIds
        .map(sampleId => {
          return {
            sampleId: sampleId,
            tags: selectedSampleTags.map(t => t.name),
          };
        })
        .concat(
          unselectedSampleIds.map(sampleId => {
            return {
              sampleId: sampleId,
              tags: unselectedSampleTags.map(t => t.name),
            };
          })
        )
        .filter(r => r.tags.length > 0);

      await AddTagsForSamples(researchProjectId, tagRequests, accessToken);
    }

    const unselectedSampleIds = (samplesToReview ?? [])
      .map(sample => sample.sampleId)
      .filter(id => !selectedSampleIds.includes(id));

    await onConfirm?.(selectedSampleIds, unselectedSampleIds, willTagSamples, (samplesToReview ?? []).length === 0);
    resetDialog();
  }, [
    accessToken,
    onConfirm,
    resetDialog,
    researchProjectId,
    selectedSampleIds,
    samplesToReview,
    selectedSampleTags,
    unselectedSampleTags,
    willTagSamples,
  ]);

  useEffect(() => {
    const isModalOpen = open || modalOpen;
    // Don't do anything unless the modal is open and there's something to check/load
    if (!accessToken || !isModalOpen || rawData !== undefined) {
      return;
    }

    return LoadState(setLoadingState, async () => {
      // if no samples are provided, use the sample Ids from the input to fetch the data from the server
      const data = await CheckPatientSamplesAlreadyProcessed(sampleIds, accessToken);

      if (
        !autoConfirmWhenNoResultsFound &&
        data &&
        Object.keys(data).length === 0 &&
        !Object.values(samplesByPatientDeidentity).some(s => s.length > 1)
      ) {
        // If nothing found, just confirm and close
        setRawData({});
        await handleConfirm();
        return;
      }

      setRawData(data);
      setSampleTagOptions(await GetSampleTags(accessToken));
    });
  }, [
    accessToken,
    modalOpen,
    open,
    samplesToReview,
    sampleIds,
    setRawData,
    setSampleTagOptions,
    handleConfirm,
    autoConfirmWhenNoResultsFound,
    samplesByPatientDeidentity,
    rawData,
  ]);

  useEffect(() => {
    if (loadingState.status !== 'Loading' || rawData === undefined) {
      return;
    }

    const toRow = (
      sample: SelectedSampleInfo,
      patientDeidentity: string,
      duplicateSelected: boolean,
      sampleInformation?: SampleTrackingSampleInformation[]
    ): DuplicateSampleRow => {
      const entryIds: { [key: string]: string } = {};

      const siblings = (sampleInformation ?? []).map(info => {
        const entryId = entryIds[info.sampleData.sample.sampleId] ?? uuidv4();
        entryIds[info.sampleData.sample.sampleId] = entryId;

        return {
          sampleId: info.sampleData.sample.sampleId,
          entryId: entryId,
          sampleIdentifier: getTransitionSampleIdentifier(
            info.sampleData.labAssignedSampleLabel,
            info.sampleData.sample
          ),
          researchProject: info.researchProject.name,
          transition: info.configuredTransition.displayName ?? info.transition.transition,
          checkByStatus: info.sampleData.transitionSample.checkByStatus,
          qcStatus: info.sampleData.transitionSample.qcStatus,
        } as SampleDetail;
      });

      return {
        sampleId: sample.sampleId,
        sampleIdentifier: sample.sampleIdentifier,
        patientDeidentity: patientDeidentity,
        siblings: siblings,
        duplicateSelected: duplicateSelected,
      } as DuplicateSampleRow;
    };

    const sampleIdsProcessed = new Set<string>();

    let toReview = flatten(
      Object.entries(rawData ?? {}).map(([deid, sampleInformation]) => {
        // this means we have multiple samples in the current selection that have the same patient deidentity
        // we'll show a warning icon in the grid for these cases
        const duplicateSelected = samplesByPatientDeidentity[deid].length > 1;

        return samplesByPatientDeidentity[deid].map(sample => {
          sampleIdsProcessed.add(sample.sampleId);

          return toRow(sample, deid, duplicateSelected, sampleInformation as SampleTrackingSampleInformation[]);
        });
      })
    );

    // now, need to add any samples we haven't already seen, where there are > 1 per deidentity
    const duplicatesInSelection = samplesToCheck
      .filter(
        sample =>
          !sampleIdsProcessed.has(sample.sampleId) && samplesByPatientDeidentity[sample.patientDeidentity!]?.length > 1
      )
      .map(sample => {
        return toRow(sample, sample.patientDeidentity!, true);
      })
      .filter((s): s is DuplicateSampleRow => s !== null);

    toReview = toReview.concat(duplicatesInSelection);

    if (toReview.length > 0) {
      setSamplesToReview(toReview);
    }
  }, [rawData, loadingState, samplesByPatientDeidentity, samplesToCheck, setSamplesToReview]);

  return (
    <>
      {displayOpenControl && (
        <DialogOpenButton
          title={t('checkForDuplicateSamples')}
          onClick={() => setModalOpen(true)}
          disabled={(samplesToCheck ?? []).length === 0}
        >
          <CallSplit />
        </DialogOpenButton>
      )}
      <PersistentDialog open={open || modalOpen} onClose={handleCancel} maxWidth={'lg'} fullWidth={true}>
        <DialogContent>
          <DuplicateSamplesGrid
            samples={samplesToReview ?? []}
            setSelectedSampleIds={setSelectedSampleIds}
            loadingState={loadingState}
            researchProjectId={researchProjectId}
          />
          <Stack direction={'column'} gap={1} sx={{ justifyContent: 'flex-start', alignItems: 'left' }}>
            {!displayOpenControl ? (
              <Typography variant='subtitle2'>{t('includeSamplesInOrderInfo')}</Typography>
            ) : (
              <Typography variant='subtitle2'>{t('checkForDuplicateSamplesSelectionInfo')}</Typography>
            )}
            {noSamplesSelected && !displayOpenControl && (
              <Stack
                direction={'row'}
                gap={1}
                sx={{ alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '10px', paddingBottom: '10px' }}
              >
                <WarningAmberIcon color={'warning'} />
                <Typography variant='subtitle2'>{t('includeSamplesInOrderEmpty')}</Typography>
              </Stack>
            )}
          </Stack>
          {(samplesToReview ?? []).length > 0 && (
            <>
              {renderTagSelection(
                t('tagSelectedSamplesWith'),
                tagSelectedSamples,
                setTagSelectedSamples,
                selectedSampleTags,
                setSelectedSampleTags,
                sampleTagOptions as SampleTag[],
                loadingState,
                { paddingRight: '15px' }
              )}
              {renderTagSelection(
                t('tagUnselectedSamplesWith'),
                tagUnselectedSamples,
                setTagUnselectedSamples,
                unselectedSampleTags,
                setUnselectedSampleTags,
                sampleTagOptions as SampleTag[],
                loadingState
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <CancelButton onClick={handleCancel} />
          <PrimaryButton
            onClick={handleConfirm}
            disabled={loadingState.status === 'Loading' || loadingState.status === 'Error'}
          >
            {actionButtonLabel}
          </PrimaryButton>
        </DialogActions>
      </PersistentDialog>
    </>
  );
};
